import React, { useState } from "react";
import { Modal, Button } from "antd";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  arrayMove,
  useSortable,
} from "@dnd-kit/sortable";
import { DragOutlined } from "@ant-design/icons";

function DraggableImage({ image }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: image.id });

  const style = {
    transform: `translate3d(${transform?.x || 0}px, ${transform?.y || 0}px, 0)`,
    transition,
    opacity: isDragging ? 0.5 : 1,
    margin: 8,
    border: "1px solid #d9d9d9",
    borderRadius: 8,
    overflow: "hidden",
    position: "relative",
    width: 130,
    touchAction: "none",
  };

  const handleStyle = {
    position: "absolute",
    top: 4,
    right: 4,
    background: "rgba(0,0,0,0.55)",
    color: "#fff",
    borderRadius: 4,
    padding: "2px 6px",
    fontSize: 12,
    cursor: "grab",
    display: "flex",
    alignItems: "center",
    gap: 4,
    zIndex: 1,
    userSelect: "none",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <div style={handleStyle} {...listeners}>
        <DragOutlined /> drag
      </div>
      <img
        src={image.path}
        alt=""
        style={{ width: 130, height: 110, objectFit: "cover", display: "block" }}
        draggable={false}
      />
    </div>
  );
}

function ReorderImagesModal({ record, onClose, onSave, saving }) {
  const [images, setImages] = useState([...record.images]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) {
      const oldIndex = images.findIndex((img) => img.id === active.id);
      const newIndex = images.findIndex((img) => img.id === over.id);
      setImages(arrayMove(images, oldIndex, newIndex));
    }
  };

  const handleSave = () => {
    onSave(record.id, images.map((img) => img.id));
  };

  return (
    <Modal
      title={`Reorder Images — ${record.title}`}
      open={true}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>
          Save Order
        </Button>,
      ]}
      width={700}
    >
      <p style={{ color: "#888", marginBottom: 12 }}>
        Drag the images to reorder them.
      </p>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={images.map((img) => img.id)}
          strategy={rectSortingStrategy}
        >
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {images.map((image) => (
              <DraggableImage key={image.id} image={image} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </Modal>
  );
}

export default ReorderImagesModal;
